/**
 * Copyright 2026 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import { login } from 'api/auth';
import { FormEvent, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentUser } from 'redux/actions';
import { IStore } from 'redux/storeType';

/**
 * Login page authenticating against the FrontendAuth backend endpoint.
 * On success the user info is stored in Redux and the user is redirected to /account.
 */
function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const currentUser = useSelector((state: IStore) => state.user?.CurrentUser);
  const initialized = useSelector((state: IStore) => state.user?.initialized);

  if (initialized && currentUser) {
    return <Navigate to="/account" replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(undefined);

    try {
      const user = await login(username, password);
      dispatch(setCurrentUser(user));
      navigate('/account');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Login failed');
    }
  };

  return (
    <main className="container">
      <div className="row vh-100 d-flex justify-content-center align-items-center">
        <div className="col-12 col-md-8 col-lg-6">
          <div className="border border-3 border-primary" />
          <div className="card shadow">
            <div className="card-body">
              <div className="mb-3 mt-md-4">
                <h2 className="fw-bold mb-2">umati Dashboard</h2>
                <p className="mb-3">Please enter your username and password.</p>
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="login-username" className="form-label">
                      Username
                    </label>
                    <input
                      id="login-username"
                      type="text"
                      className="form-control"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      autoComplete="username"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="login-password" className="form-label">
                      Password
                    </label>
                    <input
                      id="login-password"
                      type="password"
                      className="form-control"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">
                    Login
                  </button>
                  {errorMessage && (
                    <p role="alert" className="text-danger mt-3 mb-0">
                      {errorMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Login;
